import { useMemo } from "react";
import { FormTemplate } from "../../types/formTemplates";

export type TemplateSort =
  | "last_modified"
  | "alphabetical"
  | "created"
  | "most_used"
  | "most_recent_used";

export const DEFAULT_TEMPLATE_SORT: TemplateSort = "last_modified";

export const TEMPLATE_SORT_OPTIONS: { value: TemplateSort; label: string }[] = [
  { value: "last_modified", label: "Last modified" },
  { value: "alphabetical", label: "Alphabetical (A → Z)" },
  { value: "created", label: "Newest" },
  { value: "most_used", label: "Most used" },
  { value: "most_recent_used", label: "Most recently used" },
];

function timestampMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function compareNullableDescNullsLast(
  a: number | null,
  b: number | null,
): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

function compareNameAsc(a: FormTemplate, b: FormTemplate): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

function compareIdAsc(a: FormTemplate, b: FormTemplate): number {
  return a.id.localeCompare(b.id);
}

// All sort modes break ties by name (ASC) then id (ASC) so two templates
// with the same primary value land in a predictable, human-meaningful order.
function withTiebreakers(
  primary: number,
  a: FormTemplate,
  b: FormTemplate,
): number {
  return primary || compareNameAsc(a, b) || compareIdAsc(a, b);
}

export function sortTemplates(
  templates: FormTemplate[],
  sortBy: TemplateSort,
): FormTemplate[] {
  const copy = [...templates];
  switch (sortBy) {
    case "alphabetical":
      copy.sort((a, b) => withTiebreakers(0, a, b));
      break;
    case "created":
      copy.sort((a, b) =>
        withTiebreakers(
          compareNullableDescNullsLast(
            timestampMs(a.created_dt),
            timestampMs(b.created_dt),
          ),
          a,
          b,
        ),
      );
      break;
    case "most_used":
      copy.sort((a, b) =>
        withTiebreakers((b.use_count ?? 0) - (a.use_count ?? 0), a, b),
      );
      break;
    case "most_recent_used":
      copy.sort((a, b) =>
        withTiebreakers(
          compareNullableDescNullsLast(
            timestampMs(a.last_used_at),
            timestampMs(b.last_used_at),
          ),
          a,
          b,
        ),
      );
      break;
    case "last_modified":
    default:
      copy.sort((a, b) =>
        withTiebreakers(
          compareNullableDescNullsLast(
            timestampMs(a.updated_dt),
            timestampMs(b.updated_dt),
          ),
          a,
          b,
        ),
      );
      break;
  }
  return copy;
}

export function useSortedTemplates(
  templates: FormTemplate[],
  sortBy: TemplateSort,
): FormTemplate[] {
  return useMemo(() => sortTemplates(templates, sortBy), [templates, sortBy]);
}
